import { useEffect, useMemo, useRef, useState } from "react";
import { api, AUD, AUDc } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, Pencil, Check, X, UploadSimple, DownloadSimple, Paperclip, FileText } from "@phosphor-icons/react";
import { isDemoMode } from "../lib/demoStore";

const TAX_YEARS = [2024, 2025, 2026, 2027];

const IS_DEMO = () => isDemoMode();
const ENTITY_ORDER_LIVE = ["darren", "penelope", "pendaz", "smsf"];
const ENTITY_ORDER_DEMO = ["partner_a", "partner_b", "family_business", "smsf"];
const ENTITY_LABELS_LIVE = {
    darren: "Darren (Personal)",
    penelope: "Penelope (Personal)",
    pendaz: "Pendaz (Company)",
    smsf: "SMSF",
};
const ENTITY_LABELS_DEMO = {
    partner_a: "Partner A (Personal)",
    partner_b: "Partner B (Personal)",
    family_business: "Family Business",
    smsf: "SMSF",
};
const ENTITY_TINT = {
    darren: "#4A6B7A", penelope: "#C97B5A", pendaz: "#5F8071", smsf: "#8C6E52",
    partner_a: "#4A6B7A", partner_b: "#C97B5A", family_business: "#5F8071",
};
const getEntityOrder = () => IS_DEMO() ? ENTITY_ORDER_DEMO : ENTITY_ORDER_LIVE;
const getEntityLabels = () => IS_DEMO() ? ENTITY_LABELS_DEMO : ENTITY_LABELS_LIVE;

const emptyRow = (entity, taxYear) => ({
    entity,
    category: "work_expenses",
    date: new Date().toISOString().slice(0, 10),
    vendor: "",
    amount: 0,
    notes: "",
    receipt_file_id: null,
    tax_year: taxYear,
    _receipt_filename: "",
});

export default function Tax() {
    const [taxYear, setTaxYear] = useState(2026);
    const [meta, setMeta] = useState(null);
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [activeEntity, setActiveEntity] = useState(() => getEntityOrder()[0]);
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [uploadingIdx, setUploadingIdx] = useState(null);
    const fileInputRef = useRef(null);
    const [pendingFileTarget, setPendingFileTarget] = useState(null); // 'new' | id

    const reload = async () => {
        const [m, s, l] = await Promise.all([
            meta ? Promise.resolve({ data: meta }) : api.get("/tax/meta"),
            api.get(`/tax/summary?tax_year=${taxYear}`),
            api.get(`/tax/deductions?tax_year=${taxYear}`),
        ]);
        if (!meta) setMeta(m.data);
        setSummary(s.data);
        setRows(l.data.items || []);
    };

    useEffect(() => { reload(); }, [taxYear]);

    const catLabels = meta?.category_labels || {};
    const categories = meta?.categories || [];

    const filtered = useMemo(() =>
        rows.filter(r => r.entity === activeEntity),
        [rows, activeEntity]
    );

    const entityTotal = summary?.by_entity?.[activeEntity]?.total || 0;
    const entityCount = summary?.by_entity?.[activeEntity]?.count || 0;
    const entityCats = summary?.by_entity?.[activeEntity]?.by_category || {};

    // ---- File handling ----
    const openFilePicker = (target) => {
        setPendingFileTarget(target);
        fileInputRef.current?.click();
    };

    const onFileChange = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f || !pendingFileTarget) return;
        setUploadingIdx(pendingFileTarget);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/tax/upload?ocr=true", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90000,
            });
            if (pendingFileTarget === "new") {
                const ocr = data.ocr || {};
                const ocrOk = !ocr.error && (ocr.vendor || ocr.amount);
                setDraft(d => ({
                    ...d,
                    receipt_file_id: data.id,
                    _receipt_filename: data.original_filename,
                    // Prefill from OCR only when field is still empty
                    vendor: d.vendor || ocr.vendor || "",
                    amount: (Number(d.amount) || 0) === 0 && ocr.amount ? Number(ocr.amount) : d.amount,
                    date: d.date && d.date !== new Date().toISOString().slice(0,10) ? d.date : (ocr.date || d.date),
                    category: ocr.category_hint && !d._categoryEdited ? ocr.category_hint : d.category,
                    notes: d.notes || ocr.notes || "",
                }));
                if (ocrOk) {
                    toast.success(`Receipt attached — Claude pre-filled ${ocr.vendor || ""} ${ocr.amount ? "$"+ocr.amount : ""}`.trim());
                } else if (ocr.error) {
                    toast.success("Receipt attached (OCR unavailable — please enter fields manually)");
                } else {
                    toast.success("Receipt attached");
                }
            } else {
                // attach to existing row
                const row = rows.find(r => r.id === pendingFileTarget);
                if (row) {
                    await api.put(`/tax/deductions/${row.id}`, {
                        entity: row.entity, category: row.category, date: row.date,
                        vendor: row.vendor || "", amount: Number(row.amount),
                        notes: row.notes || "", tax_year: row.tax_year,
                        receipt_file_id: data.id,
                    });
                    toast.success("Receipt attached");
                    await reload();
                }
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadingIdx(null);
            setPendingFileTarget(null);
        }
    };

    // ---- CRUD ----
    const startNew = () => {
        setDraft(emptyRow(activeEntity, taxYear));
        setEditingId("__new__");
    };

    const startEdit = (r) => {
        setDraft({ ...r, _receipt_filename: r._receipt_filename || "" });
        setEditingId(r.id);
    };

    const cancelEdit = () => { setEditingId(null); setDraft(null); };

    const saveDraft = async () => {
        if (!draft) return;
        if (!draft.date || !draft.amount) {
            toast.error("Date and amount are required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                entity: draft.entity, category: draft.category, date: draft.date,
                vendor: draft.vendor || "", amount: Number(draft.amount),
                notes: draft.notes || "", tax_year: draft.tax_year,
                receipt_file_id: draft.receipt_file_id || null,
            };
            if (editingId === "__new__") {
                await api.post("/tax/deductions", payload);
                toast.success("Deduction added");
            } else {
                await api.put(`/tax/deductions/${editingId}`, payload);
                toast.success("Saved");
            }
            cancelEdit();
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (r) => {
        if (!confirm(`Delete ${r.vendor || "this deduction"}?`)) return;
        try {
            await api.delete(`/tax/deductions/${r.id}`);
            await reload();
        } catch { toast.error("Delete failed"); }
    };

    const downloadReceipt = async (fid, filename) => {
        try {
            const { data } = await api.get(`/tax/files/${fid}`, { responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url; a.download = filename || "receipt";
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch { toast.error("Download failed"); }
    };

    const downloadPack = async () => {
        setExporting(true);
        try {
            const { data } = await api.get(`/tax/report-pack?tax_year=${taxYear}`,
                { responseType: "blob", timeout: 120000 });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `accountant-pack-FY${taxYear}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success("Accountant pack downloaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Export failed");
        } finally { setExporting(false); }
    };

    if (!meta || !summary) return <div className="text-[var(--muted)]">Loading…</div>;

    return (
        <div data-testid="tax-page">
            <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                   onChange={onFileChange} style={{ display: "none" }}
                   data-testid="tax-file-input" />

            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Tax Management</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Deductions & receipts.</h1>
                    <p className="text-[var(--muted)] mt-3">
                        {IS_DEMO()
                            ? "Track deductible expenses across Partner A, Partner B, Family Business and SMSF — attach receipts, then export the Accountant Pack at year end."
                            : "Track deductible expenses across Darren, Penelope, Pendaz and SMSF — attach receipts, then export the Accountant Pack at year end."}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <label className="label-eyebrow">Tax year</label>
                    <select value={taxYear} onChange={e => setTaxYear(Number(e.target.value))}
                            className="border border-[var(--line)] px-3 py-2 rounded text-sm bg-white"
                            data-testid="tax-year-select">
                        {TAX_YEARS.map(y => <option key={y} value={y}>FY{y}</option>)}
                    </select>
                    <button className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
                            onClick={downloadPack} disabled={exporting}
                            data-testid="tax-download-pack">
                        <DownloadSimple size={14} />
                        {exporting ? "Preparing…" : "Accountant Pack (ZIP)"}
                    </button>
                </div>
            </div>

            {/* Grand total + per-entity KPIs */}
            <div className="grid md:grid-cols-4 gap-4 mb-8">
                {getEntityOrder().map(e => {
                    const t = summary.by_entity?.[e] || { total: 0, count: 0 };
                    const active = activeEntity === e;
                    return (
                        <button key={e}
                                onClick={() => setActiveEntity(e)}
                                data-testid={`tax-entity-tab-${e}`}
                                className="text-left p-6 rounded transition-all"
                                style={{
                                    background: active ? "var(--eucalyptus)" : "white",
                                    color: active ? "var(--bone)" : "var(--ink)",
                                    border: `1px solid ${active ? "var(--eucalyptus)" : "var(--line)"}`,
                                }}>
                            <div className="label-eyebrow flex items-center gap-2"
                                 style={{ color: active ? "rgba(255,255,255,0.75)" : "var(--muted)" }}>
                                <span className="inline-block w-2 h-2 rounded-full"
                                      style={{ background: active ? "var(--bone)" : ENTITY_TINT[e] }} />
                                {getEntityLabels()[e]}
                            </div>
                            <div className="font-serif text-3xl mt-3 font-mono-num">{AUD(t.total)}</div>
                            <div className="text-xs mt-1"
                                 style={{ color: active ? "rgba(255,255,255,0.65)" : "var(--muted)" }}>
                                {t.count} {t.count === 1 ? "item" : "items"}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Category breakdown for active entity */}
            <div className="hairline bg-white p-6 rounded mb-6" data-testid="tax-cat-breakdown">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <div className="label-eyebrow">By category — {getEntityLabels()[activeEntity]}</div>
                        <h2 className="font-serif text-2xl mt-1">FY{taxYear} deductions</h2>
                    </div>
                    <div className="text-right">
                        <div className="label-eyebrow">Entity total</div>
                        <div className="font-serif text-2xl font-mono-num" data-testid="tax-entity-total">
                            {AUD(entityTotal)}
                        </div>
                        <div className="text-xs text-[var(--muted)]">{entityCount} items</div>
                    </div>
                </div>
                {Object.keys(entityCats).length === 0 ? (
                    <div className="text-sm text-[var(--muted)] italic py-6">
                        No deductions yet for FY{taxYear}. Add one below.
                    </div>
                ) : (
                    <div className="grid md:grid-cols-3 gap-3">
                        {categories.map(c => {
                            const v = entityCats[c] || 0;
                            if (v === 0) return null;
                            const pct = entityTotal ? (v / entityTotal) * 100 : 0;
                            return (
                                <div key={c} className="p-3 rounded"
                                     style={{ background: "var(--line)40" }}
                                     data-testid={`tax-cat-${c}`}>
                                    <div className="label-eyebrow">{catLabels[c]}</div>
                                    <div className="font-mono-num text-lg mt-1">{AUDc(v)}</div>
                                    <div className="mt-2 h-1 rounded overflow-hidden"
                                         style={{ background: "var(--line)" }}>
                                        <div style={{ width: `${pct}%`, height: "100%",
                                                       background: ENTITY_TINT[activeEntity] }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Deductions table */}
            <div className="hairline bg-white rounded">
                <div className="p-6 border-b flex items-center justify-between"
                     style={{ borderColor: "var(--line)" }}>
                    <div>
                        <div className="label-eyebrow">Ledger</div>
                        <h2 className="font-serif text-2xl mt-1">
                            {getEntityLabels()[activeEntity]} — deductions
                        </h2>
                    </div>
                    <button className="btn-primary text-sm flex items-center gap-2"
                            onClick={startNew} disabled={editingId === "__new__"}
                            data-testid="tax-add-btn">
                        <Plus size={14} /> Add deduction
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="editorial w-full text-sm" data-testid="tax-table">
                        <thead>
                        <tr>
                            <th>Date</th><th>Category</th><th>Vendor</th>
                            <th className="num">Amount</th><th>Notes</th>
                            <th>Receipt</th><th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {editingId === "__new__" && (
                            <DraftRow draft={draft} setDraft={setDraft} catLabels={catLabels}
                                      categories={categories} onSave={saveDraft} onCancel={cancelEdit}
                                      saving={saving} onAttach={() => openFilePicker("new")}
                                      uploading={uploadingIdx === "new"}
                                      testId="tax-draft-row" />
                        )}
                        {filtered.map(r => (
                            editingId === r.id ? (
                                <DraftRow key={r.id} draft={draft} setDraft={setDraft}
                                          catLabels={catLabels} categories={categories}
                                          onSave={saveDraft} onCancel={cancelEdit}
                                          saving={saving} onAttach={() => openFilePicker("new")}
                                          uploading={uploadingIdx === "new"}
                                          testId={`tax-edit-row-${r.id}`} />
                            ) : (
                                <tr key={r.id} data-testid={`tax-row-${r.id}`}>
                                    <td>{r.date}</td>
                                    <td>{catLabels[r.category] || r.category}</td>
                                    <td>{r.vendor || "—"}</td>
                                    <td className="num font-mono-num">{AUDc(r.amount)}</td>
                                    <td className="text-[var(--muted)]">{r.notes || "—"}</td>
                                    <td>
                                        {r.receipt_file_id ? (
                                            <button onClick={() => downloadReceipt(r.receipt_file_id)}
                                                    className="inline-flex items-center gap-1 text-[var(--eucalyptus)] hover:underline"
                                                    data-testid={`tax-receipt-view-${r.id}`}>
                                                <FileText size={14} /> View
                                            </button>
                                        ) : (
                                            <button onClick={() => openFilePicker(r.id)}
                                                    className="inline-flex items-center gap-1 text-[var(--muted)] hover:text-[var(--ink)]"
                                                    disabled={uploadingIdx === r.id}
                                                    data-testid={`tax-receipt-attach-${r.id}`}>
                                                <Paperclip size={14} />
                                                {uploadingIdx === r.id ? "…" : "Attach"}
                                            </button>
                                        )}
                                    </td>
                                    <td>
                                        <div className="flex gap-2 justify-end">
                                            <button onClick={() => startEdit(r)}
                                                    className="text-[var(--muted)] hover:text-[var(--ink)]"
                                                    data-testid={`tax-edit-${r.id}`}>
                                                <Pencil size={14} />
                                            </button>
                                            <button onClick={() => remove(r)}
                                                    className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                    data-testid={`tax-del-${r.id}`}>
                                                <Trash size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )
                        ))}
                        {!editingId && filtered.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-12 text-[var(--muted)] italic">
                                No deductions yet — click &quot;Add deduction&quot; to start.
                            </td></tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function DraftRow({ draft, setDraft, categories, catLabels, onSave, onCancel, saving, onAttach, uploading, testId }) {
    if (!draft) return null;
    const patch = (k, v) => setDraft(d => ({ ...d, [k]: v }));
    return (
        <tr data-testid={testId} style={{ background: "var(--line)20" }}>
            <td>
                <input type="date" value={draft.date}
                       onChange={e => patch("date", e.target.value)}
                       className="border border-[var(--line)] px-2 py-1 rounded text-sm"
                       data-testid="tax-draft-date" />
            </td>
            <td>
                <select value={draft.category}
                        onChange={e => patch("category", e.target.value)}
                        className="border border-[var(--line)] px-2 py-1 rounded text-sm"
                        data-testid="tax-draft-category">
                    {categories.map(c => <option key={c} value={c}>{catLabels[c]}</option>)}
                </select>
            </td>
            <td>
                <input type="text" value={draft.vendor} placeholder="Vendor"
                       onChange={e => patch("vendor", e.target.value)}
                       className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full"
                       data-testid="tax-draft-vendor" />
            </td>
            <td className="num">
                <input type="number" step="0.01" value={draft.amount}
                       onChange={e => patch("amount", e.target.value)}
                       className="border border-[var(--line)] px-2 py-1 rounded text-sm w-28 text-right font-mono-num"
                       data-testid="tax-draft-amount" />
            </td>
            <td>
                <input type="text" value={draft.notes} placeholder="Notes"
                       onChange={e => patch("notes", e.target.value)}
                       className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full"
                       data-testid="tax-draft-notes" />
            </td>
            <td>
                {draft.receipt_file_id ? (
                    <span className="inline-flex items-center gap-1 text-xs text-[var(--eucalyptus)]">
                        <FileText size={12} /> {draft._receipt_filename || "Attached"}
                    </span>
                ) : (
                    <button onClick={onAttach} disabled={uploading}
                            className="inline-flex items-center gap-1 text-xs text-[var(--muted)] hover:text-[var(--ink)]"
                            data-testid="tax-draft-attach">
                        <UploadSimple size={12} /> {uploading ? "Uploading…" : "Attach receipt"}
                    </button>
                )}
            </td>
            <td>
                <div className="flex gap-2 justify-end">
                    <button onClick={onSave} disabled={saving}
                            className="text-[var(--eucalyptus)] hover:opacity-80 disabled:opacity-40"
                            data-testid="tax-draft-save">
                        <Check size={16} />
                    </button>
                    <button onClick={onCancel}
                            className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                            data-testid="tax-draft-cancel">
                        <X size={16} />
                    </button>
                </div>
            </td>
        </tr>
    );
}
